// The intake card as a real 5x7 PDF, drawn with the same measurements as IntakeCard's CSS (inches, from the top-left).
// Why a PDF: the iPad's browser adds its own margins and a URL / date footer to any printed web page and shrinks it;
// a PDF sent to the share sheet's Print prints at exact size with nothing added (23 Sep).
import type { CardData } from '../components/IntakeCard'
import { lines } from '../components/IntakeCard'
import { LABEL } from './types'
import type { Ownership } from './types'

const IN = 72
const PAGE_W = 5 * IN, PAGE_H = 7 * IN
const L = 0.28 * IN, W = 4.44 * IN, R = L + W
const SIDE_X = L + W * 0.66

export async function cardPdfBytes(c: CardData): Promise<Uint8Array> {
  const { PDFDocument, StandardFonts, rgb, LineCapStyle } = await import('pdf-lib')
  const doc = await PDFDocument.create()
  doc.setTitle(`Starchive intake card ${c.intakeNo}`)
  const page = doc.addPage([PAGE_W, PAGE_H])
  const bold = await doc.embedFont(StandardFonts.HelveticaBold)
  const black = rgb(0, 0, 0), ink = rgb(0x22 / 255, 0x22 / 255, 0x22 / 255), grey = rgb(0x8a / 255, 0x8a / 255, 0x8a / 255)
  const dot = rgb(0xbb / 255, 0xbb / 255, 0xbb / 255), white = rgb(1, 1, 1)
  const y = (top: number) => PAGE_H - top

  const clean = (s: string) => Array.from(s ?? '').map((ch) => { try { bold.widthOfTextAtSize(ch, 10); return ch } catch { return '?' } }).join('')
  const width = (s: string, size: number, sp = 0) => bold.widthOfTextAtSize(s, size) + sp * s.length
  const text = (s: string, x: number, base: number, size: number, color = black, sp = 0) => {
    s = clean(s)
    if (!sp) { page.drawText(s, { x, y: y(base), size, font: bold, color }); return }
    let cx = x
    for (const ch of s) { page.drawText(ch, { x: cx, y: y(base), size, font: bold, color }); cx += bold.widthOfTextAtSize(ch, size) + sp }
  }
  const fit = (s: string, size: number, max: number) => {
    s = clean(s)
    if (width(s, size) <= max) return s
    while (s.length && width(s + '…', size) > max) s = s.slice(0, -1)
    return s + '…'
  }
  const hline = (top: number, x1: number, x2: number, t: number, color = ink) =>
    page.drawLine({ start: { x: x1, y: y(top) }, end: { x: x2, y: y(top) }, thickness: t, color })
  const label = (s: string, x: number, rowTop: number) => text(s.toUpperCase(), x, rowTop + 9.2, 6.3, grey, 1.1)
  const val = (s: string, x: number, rowTop: number, max: number) => text(fit(s, 10, max), x, rowTop + 22.7, 10)

  // one row of tick options; widths in inches (the last one takes what it needs)
  const opts = (items: { t: string; on: boolean; w?: number }[], x: number, top: number) => {
    let cx = x
    const mid = top + 4.95
    for (const o of items) {
      const bt = mid - 3.75
      page.drawRectangle({ x: cx + 0.4, y: y(bt + 7.5 - 0.4), width: 6.7, height: 6.7, borderColor: black, borderWidth: 0.8, color: o.on ? black : undefined })
      if (o.on) {
        page.drawLine({ start: { x: cx + 1.9, y: y(bt + 3.9) }, end: { x: cx + 3.2, y: y(bt + 5.4) }, thickness: 1.1, color: white, lineCap: LineCapStyle.Round })
        page.drawLine({ start: { x: cx + 3.2, y: y(bt + 5.4) }, end: { x: cx + 5.8, y: y(bt + 2.0) }, thickness: 1.1, color: white, lineCap: LineCapStyle.Round })
      }
      text(o.t, cx + 7.5 + 3.5, mid + 3.0, 8.6)
      cx += o.w ? o.w * IN : 0
    }
  }
  const ruled = (s: string, rowTop: number, perLine: number) => {
    lines(s, 2, perLine).forEach((ln, i) => {
      const top = rowTop + 12.24 + i * 15.12
      page.drawLine({ start: { x: L, y: y(top + 14.87) }, end: { x: R, y: y(top + 14.87) }, thickness: 0.5, color: dot, dashArray: [0.6, 1.2] })
      if (ln) text(fit(ln, 9.5, W), L, top + 10.8, 9.5)
    })
  }

  // header: wordmark left, INTAKE CARD and the number right, rule under
  try {
    const png = await fetch('/brand/wordmark-black.png').then((r) => r.arrayBuffer())
    const img = await doc.embedPng(png)
    const iw = 1.45 * IN, ih = iw * img.height / img.width
    page.drawImage(img, { x: L, y: y(96.5), width: iw, height: ih })
  } catch { text('STARCHIVE', L, 94, 14) }
  const t1 = 'INTAKE CARD'
  text(t1, R - width(t1, 8, 2.2), 81, 8, black, 2.2)
  const no = typeof c.intakeNo === 'number' ? String(c.intakeNo).padStart(4, '0') : String(c.intakeNo ?? '')
  const tail = c.tradeNo ? ` · TRADE #${c.tradeNo}` : ''
  const noW = width(no, 9, 0.5) + (tail ? width(tail, 6.3, 1.1) + 3 : 0)
  const pre = 'INTAKE #'
  let nx = R - noW - 3 - width(pre, 6.3, 1.1)
  text(pre, nx, 94, 6.3, grey, 1.1); nx += width(pre, 6.3, 1.1) + 3
  text(no, nx, 94, 9, black, 0.5); nx += width(no, 9, 0.5)
  if (tail) text(tail, nx + 3, 94, 6.3, black, 1.1)
  hline(104.42, L, R, 1.4)

  // rows
  const rows: [number, number][] = [] // [top, height]
  let top = 1.48 * IN
  for (const h of [0.44, 0.44, 0.44, 0.44, 0.66, 0.66, 0.44, 0.44, 0.66, 0.6]) { rows.push([top, h * IN]); top += h * IN }
  rows.forEach(([t, h], i) => hline(t + h - (i === 9 ? 0.7 : 0.4), L, R, i === 9 ? 1.4 : 0.8))
  const sideTop = rows[0][0], sideBottom = rows[1][0] + rows[1][1] - 0.8
  page.drawLine({ start: { x: SIDE_X + 0.4, y: y(sideTop) }, end: { x: SIDE_X + 0.4, y: y(sideBottom) }, thickness: 0.8, color: ink })
  const sx = SIDE_X + 0.8 + 8, mainMax = SIDE_X - L - 6, sideMax = R - sx

  let [r] = rows[0]
  label('How it came in', L, r)
  opts([{ t: 'Bought', on: c.how === 'bought', w: 0.78 }, { t: 'Consigned', on: c.how === 'consigned', w: 0.9 }, { t: 'Traded', on: c.how === 'traded' }], L, r + 14.94)
  label('Date', sx, r); val(c.date, sx, r, sideMax)
  ;[r] = rows[1]
  label('Who it came from', L, r); val(c.from, L, r, mainMax)
  label('Employee ID', sx, r); val(c.id, sx, r, sideMax)
  ;[r] = rows[2]
  label('Cost or deal', L, r); val(c.deal, L, r, W)
  ;[r] = rows[3]
  label('Whose piece', L, r)
  opts((['shane', 'masih', 'split', 'consignor'] as Ownership[]).map((w) => ({ t: LABEL.whose[w], on: c.whose === w, w: 0.9 })), L, r + 14.94)
  ;[r] = rows[4]
  label('If consigned or traded, the deal in a sentence', L, r); ruled(c.sentence, r, 62)
  ;[r] = rows[5]
  label('What it is — brand and item', L, r); ruled(c.what, r, 58)
  ;[r] = rows[6]
  label('Authenticated by Christian', L, r)
  opts([{ t: 'Yes', on: !!c.authenticated, w: 0.9 }], L, r + 14.94)
  ;[r] = rows[7]
  label('Condition', L, r)
  opts([{ t: 'New w/ tags', on: c.cond === 'nwt', w: 1.12 }, { t: 'Excellent', on: c.cond === 'excellent', w: 1.0 }, { t: 'Good', on: c.cond === 'good', w: 0.88 }, { t: 'Fair', on: c.cond === 'fair' }], L, r + 14.94)
  ;[r] = rows[8]
  label('Measurements', L, r); ruled(c.measurements ?? '', r, 62)
  ;[r] = rows[9]
  label('Tick as you finish', L, r)
  opts([{ t: 'Steamed', on: false, w: 1.2 }, { t: 'Measured', on: false, w: 1.2 }, { t: 'Listed', on: false, w: 1.2 }], L, r + 14.94)
  opts([{ t: 'Photographed', on: false, w: 1.2 }, { t: 'On the floor', on: false, w: 1.2 }], L, r + 14.94 + 13.5)

  return doc.save()
}

export async function cardPdfFile(c: CardData): Promise<File> {
  const bytes = await cardPdfBytes(c)
  const no = typeof c.intakeNo === 'number' ? String(c.intakeNo).padStart(4, '0') : String(c.intakeNo ?? 'card')
  return new File([bytes as BlobPart], `Starchive-Intake-${no}.pdf`, { type: 'application/pdf' })
}

/** Hands the PDF to the iPad's share sheet (Print is in it). Must run inside a tap. Falls back to opening the PDF. */
export async function shareCardPdf(file: File): Promise<'shared' | 'opened' | 'cancelled'> {
  const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean }
  if (nav.share && nav.canShare?.({ files: [file] })) {
    try { await nav.share({ files: [file] }); return 'shared' } catch (e) { if ((e as Error)?.name === 'AbortError') return 'cancelled'; }
  }
  const url = URL.createObjectURL(file)
  window.open(url, '_blank')
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
  return 'opened'
}
